import { useEffect, useState } from 'react'

export type ThemeChoice = 'sistema' | 'claro' | 'escuro'
export type Theme = 'claro' | 'escuro'

const KEY = 'transp-demo:tema'

/**
 * Tema da aplicacao, com tres estados e dois resultados.
 *
 * `choice` e o que a pessoa pediu, e pode ser "sistema"; `theme` e o que sai na tela,
 * que so pode ser claro ou escuro. O mapa e o deck.gl precisam do SEGUNDO - eles nao
 * tem como perguntar ao CSS qual cor venceu, entao a decisao tem que ser resolvida
 * aqui, num lugar so, e descer pronta.
 *
 * Enquanto a escolha for "sistema" a tela acompanha a troca do sistema ao vivo (o
 * usuario mudando o tema do sistema operacional com a pagina aberta). A partir do
 * momento em que alguem escolhe, a escolha manda e fica guardada.
 */
export function useTheme(): {
	theme: Theme
	choice: ThemeChoice
	setChoice: (c: ThemeChoice) => void
} {
	const [choice, setChoice] = useState<ThemeChoice>(readChoice)
	const [systemDark, setSystemDark] = useState(prefersDark)

	// `matchMedia` so existe no navegador; e o unico ponto do modulo que toca nele
	// fora de um efeito, entao a leitura inicial e protegida em `prefersDark`.
	useEffect(() => {
		const mq = window.matchMedia('(prefers-color-scheme: dark)')
		const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches)
		mq.addEventListener('change', onChange)
		return () => mq.removeEventListener('change', onChange)
	}, [])

	const theme: Theme = choice === 'sistema' ? (systemDark ? 'escuro' : 'claro') : choice

	useEffect(() => {
		const root = document.documentElement
		// Sem atributo quando a escolha e "sistema": o CSS ja resolve isso pela media
		// query, e carimbar o valor resolvido congelaria a tela caso o sistema mude.
		if (choice === 'sistema') root.removeAttribute('data-theme')
		else root.dataset.theme = choice === 'escuro' ? 'dark' : 'light'

		try {
			if (choice === 'sistema') localStorage.removeItem(KEY)
			else localStorage.setItem(KEY, choice)
		} catch {
			// Navegador com armazenamento bloqueado (janela anonima, politica de site).
			// A troca continua valendo nesta sessao; so nao sobrevive ao recarregar.
		}
	}, [choice])

	return { theme, choice, setChoice }
}

/**
 * Escolha inicial: a URL manda, depois o que ficou guardado, depois o sistema.
 *
 * `?tema=claro|escuro|sistema` existe por duas razoes praticas. A primeira e poder mandar
 * a alguem um link que abre no tema certo, sem instrucao anexa. A segunda e conferencia:
 * o tema mora em `localStorage`, que e por navegador, e sem um jeito de pedir o tema pela
 * URL nao ha como abrir a tela no tema claro num navegador limpo - que e exatamente o que
 * uma verificacao automatizada faz. Defeito de tema claro passou despercebido por sessoes
 * justamente porque conferir dava trabalho.
 *
 * A URL nao e gravada: e um pedido para esta visita, nao uma troca de preferencia.
 */
function readChoice(): ThemeChoice {
	try {
		const pedido = new URLSearchParams(window.location.search).get('tema')
		if (pedido === 'claro' || pedido === 'escuro' || pedido === 'sistema') return pedido
	} catch {
		// URL sem query legivel; segue para as outras fontes.
	}
	try {
		const v = localStorage.getItem(KEY)
		if (v === 'claro' || v === 'escuro') return v
	} catch {
		// Sem armazenamento (janela anonima, politica de site): comeca seguindo o sistema.
	}
	return 'sistema'
}

function prefersDark(): boolean {
	return (
		typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches
	)
}
